// Light or dark for the logged-in app. The preference ("light", "dark" or
// "system") lives on the user's profile and is cached in localStorage so the
// inline script in public/index.html can set it before first paint. Pages
// outside /c stay light.

export const APPEARANCES = ["light", "dark", "system"];
export const DEFAULT_APPEARANCE = "light";
export const APPEARANCE_KEY = "hugo_appearance";

// Styles that apply only in dark mode, for one-off colours with no token:
// sx={{ bgcolor: "#FAF8FF", ...inDark({ bgcolor: "#1E1833" }) }}
export const DARK_SELECTOR = 'html[data-theme="dark"] &';
export const inDark = (styles) => ({ [DARK_SELECTOR]: styles });

// Spread on an element that must stay light in dark mode (the CV paper, a
// document preview). Resets the colour variables for everything inside it.
export const lightScope = { "data-theme": "light" };

const SWITCH_CLASS = "theme-switching";
const SWITCH_MS = 320;
const DARK_QUERY = "(prefers-color-scheme: dark)";

export const normaliseAppearance = (value) => (APPEARANCES.includes(value) ? value : DEFAULT_APPEARANCE);

export const systemPrefersDark = () => {
  try {
    return !!(window.matchMedia && window.matchMedia(DARK_QUERY).matches);
  } catch (_e) {
    return false;
  }
};

export const resolveScheme = (appearance, prefersDark = systemPrefersDark()) => {
  const pref = normaliseAppearance(appearance);
  if (pref === "system") return prefersDark ? "dark" : "light";
  return pref;
};

export const readStoredAppearance = () => {
  try {
    return normaliseAppearance(window.localStorage.getItem(APPEARANCE_KEY));
  } catch (_e) {
    return DEFAULT_APPEARANCE;
  }
};

export const storeAppearance = (appearance) => {
  try {
    window.localStorage.setItem(APPEARANCE_KEY, normaliseAppearance(appearance));
  } catch (_e) {
    // Private mode or storage full: the profile still has it.
  }
};

const reducedMotion = () => {
  try {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  } catch (_e) {
    return false;
  }
};

let switchTimer;

// Sets (or clears) the scheme on <html>. `animate` cross-fades colours for a
// moment, unless the user asked for reduced motion.
export const applyScheme = (scheme, { animate = false } = {}) => {
  const root = document.documentElement;
  const current = root.getAttribute("data-theme") === "dark" ? "dark" : "light";
  const next = scheme === "dark" ? "dark" : "light";
  if (animate && current !== next && !reducedMotion()) {
    root.classList.add(SWITCH_CLASS);
    clearTimeout(switchTimer);
    switchTimer = setTimeout(() => root.classList.remove(SWITCH_CLASS), SWITCH_MS);
  }
  if (next === "dark") root.setAttribute("data-theme", "dark");
  else root.removeAttribute("data-theme");
};

export const clearScheme = () => applyScheme("light");

export const watchSystemScheme = (onChange) => {
  if (!window.matchMedia) return () => {};
  const mql = window.matchMedia(DARK_QUERY);
  const handler = (e) => onChange(e.matches);
  if (mql.addEventListener) mql.addEventListener("change", handler);
  else if (mql.addListener) mql.addListener(handler);
  return () => {
    if (mql.removeEventListener) mql.removeEventListener("change", handler);
    else if (mql.removeListener) mql.removeListener(handler);
  };
};
